import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBox, Field } from "./common";
import { errorMessage, fmtMoney, fmtNum, toISODate } from "../dmApi";

const LIST_LIMIT = 99;
const DEFAULT_FIELDS = ["Impressions", "Reach", "Views", "Clicks", "Likes", "Comments", "Shares", "Leads"];

const emptyForm = (fields) => ({
  Metric_Date: toISODate(),
  source: "Project",
  Platform: "",
  Spend: "",
  ...Object.fromEntries(fields.map((f) => [f, ""])),
});

/** Day 4: capture daily responses (organic post / ad / overall) — upserted per source, platform and day. */
function MetricsGrid({ api, meta, projectId, posts, campaigns, onSaved }) {
  const fields = meta?.metric_fields || DEFAULT_FIELDS;
  const [form, setForm] = useState(() => emptyForm(fields));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const sources = useMemo(() => {
    const list = [{ key: "Project", label: "Project (overall)", platform: "" }];
    posts.forEach((p) =>
      list.push({
        key: `Post:${p.Post_Id}`,
        label: `Post · ${p.Platform || "—"} · ${String(p.Planned_Date || "").slice(0, 10)} · ${(p.Caption || "").slice(0, 40) || `#${p.Post_Id}`}`,
        platform: p.Platform || "",
      })
    );
    campaigns.forEach((c) =>
      (c.Ads || []).forEach((a) =>
        list.push({
          key: `Ad:${a.Ad_Id}`,
          label: `Ad · ${c.Campaign_Name} › ${a.Ad_Set_Name ? `${a.Ad_Set_Name} › ` : ""}${a.Ad_Name}`,
          platform: c.Platform,
        })
      )
    );
    return list;
  }, [posts, campaigns]);

  const platforms = useMemo(
    () => Array.from(new Set([...(meta?.organic_platforms || []), ...(meta?.ad_platforms || [])])),
    [meta]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await api.listMetrics(projectId)) || []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setSource = (e) => {
    const key = e.target.value;
    const src = sources.find((s) => s.key === key);
    setForm((p) => ({ ...p, source: key, Platform: src?.platform || p.Platform }));
  };

  const editRow = (r) =>
    setForm({
      Metric_Date: r.Metric_Date,
      source: r.Source_Type === "Project" ? "Project" : `${r.Source_Type}:${r.Source_Id}`,
      Platform: r.Platform,
      Spend: r.Spend ?? "",
      ...Object.fromEntries(fields.map((f) => [f, r[f] ?? ""])),
    });

  const submit = async (e) => {
    e.preventDefault();
    const [sourceType, sourceId] = form.source.split(":");
    const body = {
      Metric_Date: form.Metric_Date,
      Source_Type: sourceType,
      Source_Id: sourceId ? Number(sourceId) : null,
      Platform: form.Platform.trim() || null,
      Spend: form.Spend === "" ? 0 : Number(form.Spend),
      ...Object.fromEntries(fields.map((f) => [f, form[f] === "" ? 0 : Number(form[f])])),
    };
    setSaving(true);
    setError("");
    try {
      await api.saveMetrics(projectId, body);
      setForm((p) => ({ ...emptyForm(fields), Metric_Date: p.Metric_Date, source: p.source, Platform: p.Platform }));
      await load();
      onSaved?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete responses for ${r.Source_Label} on ${r.Metric_Date}?`)) return;
    try {
      await api.deleteMetric(r.Metric_Id);
      await load();
      onSaved?.();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <>
      <form className="dmp-card" onSubmit={submit}>
        <h3 className="dmp-card__title">Record daily responses</h3>
        <p className="dmp-muted" style={{ marginTop: -6 }}>
          Saving the same source, platform and date again updates that day.
        </p>
        <ErrorBox message={error} />
        <div className="dmp-form-grid">
          <Field label="Date *">
            <input type="date" required value={form.Metric_Date} onChange={set("Metric_Date")} />
          </Field>
          <Field label="Source">
            <select value={form.source} onChange={setSource}>
              {sources.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Platform *">
            <input required list="dmp-metric-platforms" value={form.Platform} onChange={set("Platform")} />
            <datalist id="dmp-metric-platforms">
              {platforms.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </Field>
          {fields.map((f) => (
            <Field key={f} label={f}>
              <input type="number" min={0} value={form[f]} onChange={set(f)} />
            </Field>
          ))}
          <Field label="Spend (₹)">
            <input type="number" min={0} step="0.01" value={form.Spend} onChange={set("Spend")} />
          </Field>
        </div>
        <div className="dmp-form-actions">
          <button type="submit" className="dmp-btn" disabled={saving}>
            {saving ? "Saving…" : "Save responses"}
          </button>
        </div>
      </form>

      <div className="dmp-card">
        <h3 className="dmp-card__title">Recorded responses</h3>
        {loading ? (
          <div className="dmp-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="dmp-empty">No responses recorded yet.</div>
        ) : (
          <div className="dmp-table-wrap">
            <table className="dmp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Platform</th>
                  {fields.map((f) => (
                    <th key={f} className="dmp-num">
                      {f}
                    </th>
                  ))}
                  <th className="dmp-num">Spend</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.Metric_Id}>
                    <td style={{ whiteSpace: "nowrap" }}>{r.Metric_Date}</td>
                    <td>
                      <div className="dmp-muted">{r.Source_Type}</div>
                      {r.Source_Label}
                    </td>
                    <td>{r.Platform}</td>
                    {fields.map((f) => (
                      <td key={f} className="dmp-num">
                        {fmtNum(r[f])}
                      </td>
                    ))}
                    <td className="dmp-num">{fmtMoney(r.Spend)}</td>
                    <td className="dmp-muted">{r.Recorded_By_Name || "—"}</td>
                    <td>
                      <div className="dmp-item__actions" style={{ marginTop: 0 }}>
                        <button type="button" className="dmp-link" onClick={() => editRow(r)}>
                          Edit
                        </button>
                        <button type="button" className="dmp-link" style={{ color: "var(--dmp-danger)" }} onClick={() => remove(r)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length >= LIST_LIMIT ? (
              <p className="dmp-muted dmp-campaign-leads__count">Showing the latest {LIST_LIMIT} responses.</p>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}

export default MetricsGrid;
